import twplpQuery from '~layers/app/graphql/twplp.gql'
import plpPageQuery from '~layers/app/graphql/plpPage.gql'
import plpThemePageQuery from '~layers/app/graphql/plpThemePage.gql'

import type { StrapiPlpPage } from '~layers/app/types/strapi'
import type { EsProduct } from '~layers/app/types/elastic'

export function useCatalog(meta) {
  const products = useState<EsProduct[]>('products', () => [])
  const sortOptions = useState('sortOptions', () => [])
  const { lastRequestId, sessionKey, track: trackTweakwise, profileKey } = useTweakwise()

  const data = ref()
  const loading = ref(false)
  const error = ref()

  const route = useRoute()

  const { strapiData, getStrapiData } = useStrapiCms<StrapiPlpPage>(
    meta?.value?.strapi_id || '',
    'PUBLISHED',
    (meta?.value?.page_type === 'plp-theme' ? plpThemePageQuery : plpPageQuery),
    '',
  )

  const {
    search,
  } = useCatalogSearch()

  const {
    page,
    sort,
    itemsPerPage,
    totalCount,
  } = useCatalogPaginator(sortOptions)

  const {
    rawFilters,
    filtersFromRoute,
  } = useCatalogFilters(meta)

  const brandUrlMap = useState<Record<string, string>>('brandUrlMap', () => ({}))

  const variables = computed<any>(() => {
    const filterFilters = ['filter', 'sort']
    const filters = filtersFromRoute.value.filter(filter => !filterFilters.includes(filter.key))
    return {
      // catId: categoryFilter,
      search: meta?.value?.page_type === 'search' ? search.value : '',
      page: page.value,
      filters: filters, // [{"key": "category", "values": ["100012"]}]
      itemsPerPage: itemsPerPage.value,
      sort: sort.value || meta?.value?.defaultSort || null,
      profileKey: profileKey.value,
      path: route.path.replace(/^\s*\/*\s*|\s*\/*\s*$/gm, ''),
    }
  })

  async function fetchProductData() {
    loading.value = true

    const response = await useAsyncQuery<any>(twplpQuery, variables.value)

    data.value = response.data.value
    error.value = response.error.value

    if (error.value) {
      console.error(error.value)
      return
    }

    if (!data.value) {
      return
    }
    lastRequestId.value = data.value.twProductList?.requestId || null

    const path = variables.value.path
    const pathSegments = path.split('/').filter(Boolean)
    const isBrandHeadAttribute = pathSegments.length === 1
      && !meta?.value?.filters?.some((f: any) => f.key === 'tra_brand')
      && !route.query.filter

    if (isBrandHeadAttribute) {
      const esUrls: string[] = data.value.esUrlList?.urls?.map((u: any) => u.url) || []
      const urlMap: Record<string, string> = {}

      for (const url of esUrls) {
        const segments = url.replace(/^\/|\/$/g, '').split('/')
        if (segments.length === 2 && segments[0] === pathSegments[0]) {
          urlMap[segments[1]] = url.replace(/^\/|\/$/g, '')
        }
      }
      brandUrlMap.value = urlMap
    }
    else {
      brandUrlMap.value = {}
    }

    rawFilters.value = data.value.twProductList?.filters || []
    products.value = data.value.twProductList?.products?.filter((product: any) => product.id !== null) || []
    totalCount.value = data.value.twProductList?.totalCount ?? 0
    sortOptions.value = data.value?.twProductList?.sortings || []

    if (sessionKey.value) {
      trackTweakwise('pageimpression', {
        RequestId: lastRequestId.value,
        SessionKey: sessionKey.value,
      })
    }

    loading.value = false
  }

  watch(variables, (newValue, oldValue) => {
    if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
      fetchProductData()
    }
  })

  return {
    products,
    sortOptions,
    fetchProductData,
    strapiData,
    getStrapiData,
    lastRequestId,
  }
}
